import React, { useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { styled } from '@mui/material';
import { Colors } from 'library';

export interface RDropdownMenuItem {
    label: string;
    sublabel?: string;
    icon?: React.ReactNode;
    onClick?: () => void;
    to?: string;
}

interface BaseProps {
    open: boolean;
    onClose: () => void;
    trigger: React.ReactNode;
    header?: string;
    width?: number;
    align?: 'left' | 'right';
}

interface ItemsProps extends BaseProps {
    items: RDropdownMenuItem[];
    children?: never;
}

interface ChildrenProps extends BaseProps {
    items?: never;
    children: React.ReactNode;
}

type RDropdownMenuProps = ItemsProps | ChildrenProps;

export function RDropdownMenu({
    open,
    onClose,
    items,
    header,
    children,
    trigger,
    width,
    align = 'right',
}: RDropdownMenuProps) {
    const ref = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!open) return;
        const handleClick = (e: MouseEvent) => {
            const target = e.target as HTMLElement;
            if (ref.current && !ref.current.contains(target) && !target.closest('.MuiModal-root')) {
                onClose();
            }
        };
        document.addEventListener('mousedown', handleClick);
        return () => document.removeEventListener('mousedown', handleClick);
    }, [open, onClose]);

    return (
        <Wrapper ref={ref}>
            {trigger}
            {open && (
                <Menu
                    onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                    }}
                    style={{
                        ...(width ? { width } : undefined),
                        ...(align === 'left' ? { left: 0, right: 'auto' } : undefined),
                    }}
                >
                    {children ?? (
                        <>
                            {header && <MenuHeader>{header}</MenuHeader>}
                            {items?.map((item) => {
                                const content = (
                                    <>
                                        {item.icon && <MenuItemIcon>{item.icon}</MenuItemIcon>}
                                        <div>
                                            {item.label}
                                            {item.sublabel && <MenuItemSublabel>{item.sublabel}</MenuItemSublabel>}
                                        </div>
                                    </>
                                );
                                if (item.to) {
                                    return (
                                        <MenuItemLink
                                            key={item.label}
                                            to={item.to}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                onClose();
                                            }}
                                        >
                                            {content}
                                            <ChevronRight>&rsaquo;</ChevronRight>
                                        </MenuItemLink>
                                    );
                                }
                                return (
                                    <MenuItem
                                        key={item.label}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            e.preventDefault();
                                            item.onClick?.();
                                        }}
                                    >
                                        {content}
                                    </MenuItem>
                                );
                            })}
                        </>
                    )}
                </Menu>
            )}
        </Wrapper>
    );
}

const Wrapper = styled('div')({
    position: 'relative',
});

const Menu = styled('div')({
    position: 'absolute',
    top: '100%',
    right: 0,
    marginTop: 4,
    backgroundColor: Colors.white,
    border: `1px solid ${Colors.lightGrey}`,
    borderRadius: 8,
    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
    zIndex: 20,
    overflow: 'hidden',
    minWidth: 160,
});

const MenuHeader = styled('div')({
    fontSize: 11,
    fontWeight: 700,
    color: Colors.mediumGrey,
    letterSpacing: 0.5,
    padding: '8px 16px',
});

const menuItemStyles = {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    padding: '10px 16px',
    border: 'none',
    backgroundColor: 'transparent',
    cursor: 'pointer',
    fontSize: 14,
    color: Colors.darkNavy,
    textAlign: 'left' as const,
    textDecoration: 'none',
    '&:hover': {
        backgroundColor: Colors.faintGrey,
    },
    outline: 'none',
    '&:focus, &:focus-visible': { outline: 'none', boxShadow: 'none' },
};

const MenuItem = styled('button')(menuItemStyles);

const MenuItemLink = styled(Link)({
    ...menuItemStyles,
    color: Colors.darkNavy,
    textDecoration: 'none',
    '& > span:last-child': {
        opacity: 0,
        transition: 'opacity 0.15s',
    },
    '&:hover': {
        backgroundColor: Colors.faintGrey,
        color: Colors.darkNavy,
        textDecoration: 'none',
    },
    '&:hover > span:last-child': {
        opacity: 1,
    },
    '&:visited': {
        color: Colors.darkNavy,
    },
});

const ChevronRight = styled('span')({
    marginLeft: 'auto',
    fontSize: 20,
    lineHeight: 1,
    color: Colors.mediumGrey,
});

const MenuItemSublabel = styled('div')({
    fontSize: 12,
    color: Colors.mediumGrey,
    marginTop: 2,
});

const MenuItemIcon = styled('span')({
    display: 'inline-flex',
    alignItems: 'center',
    marginRight: 8,
    color: Colors.mediumGrey,
});
